import { useEffect, useRef, useState } from "react";
import {
	FileWithShare,
	JobItemEntityType,
} from "../../../../../lib/supabase/supabaseTypes";
import { LinearProgress } from "@mui/material";

import { JobItemsTable } from "./job-document-components/job-items-table/job-items-table.component";
import { JobDocumentTitle } from "./job-document-components/job-document-title.component";
import {
	JobDocumentTypeEnum,
	mapJobDocumentTypeToColor,
} from "./job-document.types";
import { JobStatusEnum } from "@/lib/types/job";
import { CustomDatePicker } from "@/dentlab/src/components/custom-date-picker/custom-date-picker.component";
import {
	PdfTemplateType,
	XmlTemplateType,
} from "@/dentlab/src/pdf-templates/document-template.types";
import { useJobDocumentActions } from "@/dentlab/src/hooks/actions/useJobDocumentActions";
import { Button } from "@/dentlab/src/components/ui/button";
import { TooltipWrapper } from "@/dentlab/src/components/ui/tooltip-wrapper";
import { JobDocumentOptionsMenu } from "./job-document-components/job-document-options-menu.component";
import { useJobStore } from "@/dentlab/src/store/Jobs";
import { FileDropDown } from "@/dentlab/src/components/file-display-components/file-dropdown.component";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { calculateJobTotalsLegacy } from "@/dentlab/src/lib/utils/calculate";
import { useHandleExportDocument } from "@/dentlab/src/hooks/useHandleExportDocument/useHandleExportDocument";
import { useHandleReExportDocument } from "@/dentlab/src/hooks/useHandleReExportDocument";
import { JobDocumentWithFiles } from "@/dentlab/src/store/Jobs/job-documents.store";
import { AkontoSelectionDialog } from "@/dentlab/src/pages/debitoren/components/akonto-selection-dialog.component";
import {
	Calculator,
	Check,
	ClipboardList,
	ClipboardPaste,
	ClipboardPlus,
} from "lucide-react";
import { Logger } from "@/lib/logger/Logger";
import { EmailCheckButton } from "./job-document-components/email-check-button.component";
import { getDefaultTemplateTypeForDocument } from "./job-document-components/job-document-utils";
import { FileViewerModal } from "@/components/src/file-viewer";
import { JobDocumentNote } from "@/dentlab/src/components/job-documents/job-document-note.component";
import { useAccountingStore } from "@/dentlab/src/store/Accounting";
import { usePostHog } from "posthog-js/react";
import { useRegionKind } from "@/dentlab/src/lib/utils/region-utils";
import { jobDocumentTypeNamePresets } from "@/dentlab/src/store/Jobs/utils";

export const getBorderColorTop = (jobDocumentType: JobDocumentTypeEnum) => {
	const color = mapJobDocumentTypeToColor[jobDocumentType] ?? "#000000";
	return color;
};

const getBorderColorOthers = () => {
	return "#E0E0E0";
};

export interface JobDocumentExportOption {
	label: string;
	type: PdfTemplateType;
}

/**
 * JobDocument - Component displaying information for a single job document
 * @param document - job document object to display, fetched from the database by the parent component
 * @param clientId - id of the client the document belongs to
 *
 * State this component has that is relevant for exporting the document:
 * - jobItems: JobItemEntityType[] - all job items that are associated with the document
 */
export const JobDocument: React.FC<{
	jobDocument: JobDocumentWithFiles;
	onDocumentDuplicated?: (documentId: number) => void;
}> = ({ jobDocument, onDocumentDuplicated }) => {
	const {
		jobDocumentLoading,
		handleExportDocument,
		handleExportAndPrintDocument,
	} = useHandleExportDocument();
	const { reExportLoading, handleReExportDocument } =
		useHandleReExportDocument();
	const { debugMode, client } = useCentralStore((state) => ({
		debugMode: state.debugMode,
		client: state.client,
	}));
	const { jobItemsForDocuments, fetchJobItems, job, updateJobDocumentDate } =
		useJobStore((state) => ({
			jobItemsForDocuments: state.jobItemsForDocuments,
			fetchJobItems: state.fetchJobItems,
			job: state.job,
			updateJobDocumentDate: state.updateJobDocumentDate,
		}));

	const { organization } = useCentralStore((state) => ({
		organization: state.organization,
	}));
	const region = useRegionKind(client);
	const [fileViewerOpen, setFileViewerOpen] = useState(false);
	const [selectedFileId, setSelectedFileId] = useState<string | null>(null);

	const [akontoDialogOpen, setAkontoDialogOpen] = useState(false);
	const [akontoDialogData, setAkontoDialogData] = useState<{
		availableAkonto: number;
		invoiceValue: number;
		currency: string;
	} | null>(null);
	const [pendingExport, setPendingExport] = useState<{
		templateType: PdfTemplateType | XmlTemplateType;
		jobItems: JobItemEntityType[];
		andPrint?: boolean;
	} | null>(null);
	const jobDocumentType = jobDocument.type as JobDocumentTypeEnum;
	const jobItems = jobItemsForDocuments?.[jobDocument.id] ?? [];

	useEffect(() => {
		fetchJobItems(jobDocument.id);
	}, []);

	const [files, setFiles] = useState<FileWithShare[]>([]);

	useEffect(() => {
		const sortedFiles = [...(jobDocument?.files_with_shares ?? [])].sort(
			(a, b) => {
				const dateA = new Date(a.created_at ?? "");
				const dateB = new Date(b.created_at ?? "");
				return dateB.getTime() - dateA.getTime();
			}
		);
		Logger.log(sortedFiles, {}, "Sorted files");
		setFiles(sortedFiles);
	}, [jobDocument?.files_with_shares]);

	const loading =
		(jobDocumentLoading && jobDocumentLoading === jobDocument.id) ||
		(reExportLoading && reExportLoading === jobDocument.id);
	const disabled = jobDocument.status !== JobStatusEnum.IN_PROGRESS;

	const handleOpenFile = (fileId: string) => {
		setSelectedFileId(fileId);
		setFileViewerOpen(true);
	};

	// Get all file IDs for the gallery
	const allFileIds = files.map((f) => f.id).filter(Boolean) as string[];

	const handleExportClick = async (
		templateType: PdfTemplateType | XmlTemplateType,
		jobItems: JobItemEntityType[],
		andPrint?: boolean
	) => {
		if (!job || !client || !organization) return;

		const isInvoiceTemplate =
			templateType === PdfTemplateType.RECHNUNG ||
			templateType === PdfTemplateType.BARZAHLUNGSQUITTUNG ||
			templateType === PdfTemplateType.KARTENZAHLUNGSQUITTUNG;

		if (isInvoiceTemplate) {
			let recipientClientId: string | null = null;
			let recipientGuarantorId: string | null = null;
			let recipientPatientId: string | null = null;

			if (job.is_paid_by_patient) {
				recipientPatientId = job.patient_id;
			} else if (job.guarantor_id) {
				recipientGuarantorId = job.guarantor_id;
			} else {
				recipientClientId = client.id;
			}

			if (
				recipientClientId ||
				recipientGuarantorId ||
				recipientPatientId
			) {
				try {
					const invoiceValue = calculateJobTotalsLegacy({
						job,
						jobDocument,
						jobItems,
						organization,
						client,
					}).total.value;

					const { getAkonto } = useAccountingStore.getState();
					const akontoAvailable = await getAkonto(
						recipientClientId,
						recipientGuarantorId,
						recipientPatientId
					);

					if (akontoAvailable > 0 && invoiceValue > 0) {
						setAkontoDialogData({
							availableAkonto: akontoAvailable,
							invoiceValue,
							currency: organization.currency_text || "CHF",
						});
						setPendingExport({ templateType, jobItems, andPrint });
						setAkontoDialogOpen(true);
						return;
					}
				} catch (error) {
					Logger.error("Error fetching akonto:" + error);
				}
			}
		}

		if (andPrint) {
			await handleExportAndPrintDocument(
				templateType,
				jobItems,
				jobDocument,
				0
			);
		} else {
			await handleExportDocument(templateType, jobItems, jobDocument, 0);
		}
	};

	const handleAkontoConfirm = async (selectedAkonto: number) => {
		setAkontoDialogOpen(false);
		if (pendingExport) {
			const exportFn = pendingExport.andPrint
				? handleExportAndPrintDocument
				: handleExportDocument;
			await exportFn(
				pendingExport.templateType,
				pendingExport.jobItems,
				jobDocument,
				selectedAkonto
			);
			setPendingExport(null);
		}
		setAkontoDialogData(null);
	};

	const handleAkontoCancel = () => {
		setAkontoDialogOpen(false);
		setPendingExport(null);
		setAkontoDialogData(null);
	};

	if (!organization) {
		return null;
	}
	if (!client) {
		return null;
	}

	const headerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const el = headerRef.current;
		if (!el) return;
		const ro = new ResizeObserver(() => {
			const height = el.getBoundingClientRect().height;
			el.parentElement?.style.setProperty(
				"--doc-header-height",
				`${height}px`
			);
		});
		ro.observe(el);
		return () => ro.disconnect();
	}, []);

	const { total } = calculateJobTotalsLegacy({
		job,
		jobDocument,
		jobItems,
		organization,
		client,
	});

	return (
		<>
			<div>
				<div
					ref={headerRef}
					className="sticky top-0 z-10 bg-white"
					style={{
						boxShadow: "0 1px 3px 0 rgba(0,0,0,0.1)",
					}}
				>
					<div
						style={{
							height: "7px",
							backgroundColor: getBorderColorTop(jobDocumentType),
						}}
					/>
					<div
						style={{
							display: "flex",
							flexDirection: "row",
							alignItems: "center",
							justifyContent: "space-between",
							padding: "4px",
						}}
					>
						<div
							style={{
								display: "flex",
								flexDirection: "row",
								alignItems: "center",
							}}
						>
							<JobDocumentTitle
								title={jobDocument.title}
								fallbackTitle={jobDocumentTypeNamePresets(
									jobDocument.type as JobDocumentTypeEnum,
									region
								)}
								id={jobDocument.id}
								status={jobDocument.status}
							/>
							{jobDocument.type !==
								JobDocumentTypeEnum.Tracing && (
								<div
									style={{
										display: "flex",
										flexDirection: "row",
										alignItems: "center",
										paddingLeft: "10px",
									}}
								>
									<CustomDatePicker
										initialValue={
											jobDocument.date || new Date()
										}
										onChange={(value) =>
											updateJobDocumentDate(
												jobDocument.id,
												jobDocument.status,
												value ?? ""
											)
										}
									/>
								</div>
							)}
							{jobDocument.type !==
								JobDocumentTypeEnum.Tracing && (
								<JobDocumentStatusChip
									status={jobDocument.status}
								/>
							)}
							{jobDocument.type !==
								JobDocumentTypeEnum.Tracing && (
								<JobDocumentNote jobDocument={jobDocument} />
							)}
						</div>
						{jobDocument.type !== JobDocumentTypeEnum.Tracing && (
							<div className="flex flex-row gap-1 items-center">
								{hasPdfFileForDocument(jobDocument) ? (
									<span className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200">
										<Check className="w-3 h-3" />
										Exportiert
									</span>
								) : (
									<>
										<CompleteAndExportButton
											jobDocument={jobDocument}
											jobItems={jobItems ?? []}
											onExport={(
												type:
													| PdfTemplateType
													| XmlTemplateType,
												jobItems: JobItemEntityType[]
											) =>
												handleExportClick(
													type,
													jobItems
												)
											}
										/>
										<ExportAndPrintButton
											jobDocument={jobDocument}
											jobItems={jobItems ?? []}
											onPrint={(type, items) =>
												handleExportClick(
													type,
													items,
													true
												)
											}
										/>
										<EmailCheckButton
											jobDocument={jobDocument}
											jobItems={jobItems ?? []}
											handleExportDocument={(
												type,
												items,
												doc
											) =>
												handleExportDocument(
													type,
													items,
													doc,
													0
												)
											}
										/>
									</>
								)}
								<JobDocumentOptionsMenu
									jobDocument={jobDocument}
									jobItems={jobItems ?? []}
									onExport={(
										type: PdfTemplateType | XmlTemplateType
									) =>
										handleExportClick(type, jobItems ?? [])
									}
									onReExport={handleReExportDocument}
									onDocumentDuplicated={onDocumentDuplicated}
								/>
							</div>
						)}
					</div>
					{loading && <LinearProgress />}
					{files?.length > 0 && (
						<div
							data-testid="exported-pdfs"
							style={{
								display: "flex",
								borderTop: `1.5px solid ${getBorderColorOthers()}`,
								padding: "3px 6px",
								overflowX: "auto",
							}}
						>
							{files?.map((file) => (
								<FileDropDown
									key={file.id}
									file={file}
									jobDocument={jobDocument}
									onOpenFile={handleOpenFile}
								/>
							))}
						</div>
					)}
				</div>
				<JobItemsTable
					jobDocumentType={jobDocument.type as JobDocumentTypeEnum}
					jobDocumentId={jobDocument.id}
					status={jobDocument.status}
					disabled={disabled}
					total={total}
				/>
				{debugMode && (
					<div>
						ID: {jobDocument.id} Type: {jobDocument.type}
					</div>
				)}
			</div>

			{/* File Viewer Modal */}
			{allFileIds.length > 0 && (
				<FileViewerModal
					fileIds={allFileIds}
					isOpen={fileViewerOpen}
					onClose={() => {
						setFileViewerOpen(false);
						setSelectedFileId(null);
					}}
					defaultFileId={selectedFileId || undefined}
					metadata={{
						jobId: job?.id ?? undefined,
						clientId: client?.id ?? undefined,
					}}
				/>
			)}

			{/* Akonto Selection Dialog */}
			<AkontoSelectionDialog
				open={akontoDialogOpen}
				onOpenChange={handleAkontoCancel}
				availableAkonto={akontoDialogData?.availableAkonto ?? 0}
				invoiceValue={akontoDialogData?.invoiceValue ?? 0}
				currency={akontoDialogData?.currency ?? "CHF"}
				onConfirm={handleAkontoConfirm}
			/>
		</>
	);
};

const DOCUMENT_STATUS_LABELS: Record<number, string> = {
	[JobStatusEnum.IN_PROGRESS]: "Pendent",
	[JobStatusEnum.COMPLETED]: "Abgeschlossen",
	[JobStatusEnum.ARCHIVED]: "Ungültig/Archiviert",
	[JobStatusEnum.BOOKED_SINGLE]: "Verbucht (Einzelrechnung)",
	[JobStatusEnum.BOOKED_MONTHLY]: "Verbucht (Sammelrechnung)",
	[JobStatusEnum.PAID_SINGLE]: "Bezahlt (Einzelrechnung)",
	[JobStatusEnum.PAID_MONTHLY]: "Bezahlt (Sammelrechnung)",
};

const JobDocumentStatusChip: React.FC<{
	status: JobStatusEnum;
}> = ({ status }) => {
	return (
		<span className="ml-2.5 inline-flex items-center rounded px-2 py-0.5 text-xs font-medium uppercase bg-[#e0e0e0] text-[rgba(0,0,0,0.87)]">
			{DOCUMENT_STATUS_LABELS[status] ?? "Unbekannt"}
		</span>
	);
};

const hasPdfFileForDocument = (jobDocument: JobDocumentWithFiles): boolean => {
	if (
		!jobDocument.files_with_shares ||
		jobDocument.files_with_shares.length === 0
	) {
		return false;
	}

	const templateType = getDefaultTemplateTypeForDocument(
		jobDocument.type as JobDocumentTypeEnum
	);

	return jobDocument.files_with_shares.some((file) => {
		const metaData = file.meta_data as Record<string, any> | null;
		if (!metaData) return false;

		return (
			metaData.type === "pdf" &&
			metaData.pdf_template_type === templateType
		);
	});
};

const CompleteAndExportButton: React.FC<{
	jobDocument: JobDocumentWithFiles;
	jobItems: JobItemEntityType[];
	onExport: (type: PdfTemplateType, jobItems: JobItemEntityType[]) => void;
}> = ({ jobDocument, jobItems, onExport }) => {
	const postHog = usePostHog();
	const { isExportJobDocumentAllowed } = useJobDocumentActions();
	const { pendingChanges } = useJobStore((state) => ({
		pendingChanges: state.pendingChanges,
	}));
	const { isAllowed, explanation } = isExportJobDocumentAllowed(
		jobDocument,
		jobItems
	);

	const handleCompletionClick = () => {
		const templateType = getDefaultTemplateTypeForDocument(
			jobDocument.type as JobDocumentTypeEnum
		);
		if (templateType) {
			postHog.capture("complete_and_export_document", {
				document_type: jobDocument.type,
				template_type: templateType,
			});
			postHog.capture("document_exported", {
				document_type: jobDocument.type,
				template_type: templateType,
			});
			const orgId = useCentralStore.getState().organization?.id;
			if (orgId) {
				postHog.group("organization", orgId, {
					$set_once: {
						first_document_exported_at: new Date().toISOString(),
					},
				});
			}
			onExport(templateType, jobItems);
		}
	};

	const hasPdf = hasPdfFileForDocument(jobDocument);

	let tooltipText = "Abschliessen und exportieren";
	let label = "Exportieren";

	let isDisabled = !isAllowed;

	if (hasPdf) {
		label = "Exportiert";
		tooltipText = "Abgeschlossen";
		isDisabled = true;
	} else if (jobDocument.status === JobStatusEnum.COMPLETED) {
		tooltipText = "PDF exportieren";
	} else if (pendingChanges) {
		tooltipText = "Änderungen werden gespeichert...";
		isDisabled = true;
	} else if (!isAllowed) {
		tooltipText = explanation;
	}

	return (
		<TooltipWrapper title={tooltipText} position="bottom">
			<span>
				<Button
					variant="outline"
					size="sm"
					className="h-7 px-2 text-xs"
					onClick={handleCompletionClick}
					// All buttons currently start being disabled once status is updated to completed; could be done in a better way ofc
					disabled={isDisabled || jobDocument.status === JobStatusEnum.COMPLETED}
					data-testid="complete-job-document-button"
					aria-label="Button to export job document and setting its status to completed"
				>
					{label}
				</Button>
			</span>
		</TooltipWrapper>
	);
};

const ExportAndPrintButton: React.FC<{
	jobDocument: JobDocumentWithFiles;
	jobItems: JobItemEntityType[];
	onPrint: (type: PdfTemplateType, jobItems: JobItemEntityType[]) => void;
}> = ({ jobDocument, jobItems, onPrint }) => {
	const { isExportJobDocumentAllowed } = useJobDocumentActions();
	const { pendingChanges } = useJobStore((state) => ({
		pendingChanges: state.pendingChanges,
	}));
	const { isAllowed, explanation } = isExportJobDocumentAllowed(
		jobDocument,
		jobItems
	);

	const hasPdf = hasPdfFileForDocument(jobDocument);

	let tooltipText = "Exportieren & Drucken";
	let isDisabled = !isAllowed;

	// To make sure user can't click while exporting, also disable when completed
	if (hasPdf || jobDocument.status === JobStatusEnum.COMPLETED) {
		isDisabled = true;
	} else if (pendingChanges) {
		tooltipText = "Änderungen werden gespeichert...";
		isDisabled = true;
	} else if (!isAllowed) {
		tooltipText = explanation;
	}

	const handleClick = () => {
		const templateType = getDefaultTemplateTypeForDocument(
			jobDocument.type as JobDocumentTypeEnum
		);
		if (templateType) {
			onPrint(templateType, jobItems);
		}
	};

	return (
		<TooltipWrapper title={tooltipText} position="bottom">
			<span>
				<Button
					variant="outline"
					size="sm"
					className="h-7 px-2 text-xs"
					onClick={handleClick}
					disabled={isDisabled}
					data-testid="print-button-toolbar"
					aria-label="Exportieren & Drucken"
				>
					Drucken
				</Button>
			</span>
		</TooltipWrapper>
	);
};

export const getJobDocumentIcon = (type: JobDocumentTypeEnum | null) => {
	if (type === JobDocumentTypeEnum.Quotation) {
		return <Calculator className="w-4 h-4" />;
	} else if (type === JobDocumentTypeEnum.DeliveryNote) {
		return <ClipboardPaste className="w-4 h-4" />;
	} else if (type === JobDocumentTypeEnum.CreditNote) {
		return <ClipboardPlus className="w-4 h-4" />;
	} else if (type === JobDocumentTypeEnum.Tracing) {
		return <ClipboardList className="w-4 h-4" />;
	}
	return null;
};
